import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { startAsyncTask } from 'src/redux/slices/async-processing/reducers'
import { useNavigationEffect, useTabNavigation } from 'src/redux/slices/navigation/hooks'
import { getProject } from 'src/redux/slices/project/endpoints'
import { isLoading } from 'src/redux/slices/project/selectors'
import { Entrypoint } from 'src/redux/slices/umibuilder/consts'
import {
  clearAllData,
  loadGroupData,
  setCloudContext,
  setEntrypoint,
} from 'src/redux/slices/umibuilder/reducers'
import { selectSummaryData } from 'src/redux/slices/umibuilder/selectors'

import { Button, Grid, LinearProgress, Typography } from '@material-ui/core'
import CallSplitIcon from '@material-ui/icons/CallSplit'
import GavelIcon from '@material-ui/icons/Gavel'
import GetAppIcon from '@material-ui/icons/GetApp'
import HomeWorkIcon from '@material-ui/icons/HomeWork'

///// Component
const ArchetypeToUMIPanel: React.FC = () => {
  const dispatch = useDispatch()
  const summary = useSelector(selectSummaryData)
  const { data: project, isLoading: projectIsLoading } = getProject.useQuery('')
  const loading = useSelector(isLoading)
  const { goToTab } = useTabNavigation()

  const loadGroupDataFromCloud = useCallback(() => {
    if (!project) return
    dispatch(clearAllData())
    dispatch(setCloudContext(true))
    dispatch(setEntrypoint({ entrypoint: Entrypoint.full }))
    dispatch(loadGroupData(project.archetypeDefinition))
  }, [dispatch, project])

  const goToArchetypeDefinition = useCallback(() => {
    loadGroupDataFromCloud()
    goToTab('../umibuilder/organize')
  }, [loadGroupDataFromCloud, goToTab])

  const goToTemplateAssignment = useCallback(() => {
    loadGroupDataFromCloud()
    goToTab('../umibuilder/assign')
  }, [loadGroupDataFromCloud, goToTab])

  const goToExport = useCallback(() => {
    if (!project) return
    loadGroupDataFromCloud()
    const { id: projectId } = project
    const body = {
      fromCloud: true,
      projectId,
    }
    dispatch(
      startAsyncTask({
        endpoint: 'assignBuildingsToArchetypes',
        body,
      }),
    )
  }, [project, dispatch])

  useNavigationEffect({
    condition: summary?.stats.Buildings != null,
    destination: '../umibuilder/context',
  })

  if (projectIsLoading || !project) return <LinearProgress />
  return (
    <Grid container alignItems='center' justifyContent='flex-start' spacing={2}>
      <Grid item>
        <Typography variant='overline'>UBEM Status: </Typography>
      </Grid>
      {!project.archetypeDefinition && (
        <Grid item>
          <Typography variant='body2'>
            You must upload a GIS file and define keyfields to extract segmentation data from before
            defining archetypes and building an UBEM.
          </Typography>
        </Grid>
      )}
      {project.archetypeDefinition && (
        <>
          <Grid item>
            <Button
              size='small'
              variant='contained'
              color='primary'
              onClick={goToArchetypeDefinition}
              endIcon={<CallSplitIcon />}
              disabled={loading}
            >
              Define Archetypes
            </Button>
          </Grid>
          <Grid item>
            <Button
              size='small'
              variant='contained'
              color='primary'
              onClick={goToTemplateAssignment}
              endIcon={<HomeWorkIcon />}
              disabled={loading}
            >
              Assign Templates
            </Button>
          </Grid>
          <Grid item>
            <Button
              size='small'
              variant='contained'
              color='primary'
              onClick={goToExport}
              endIcon={<GavelIcon />}
              disabled={loading}
            >
              Build Model
            </Button>
          </Grid>
          <Grid item>
            <Button
              size='small'
              variant='contained'
              color='primary'
              endIcon={<GetAppIcon />}
              disabled={!project?.umi_baseline_file_location || loading}
            >
              Download Model
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default ArchetypeToUMIPanel
